import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
const corevestDark = '/logos/corvest-dark-logo.png';
const corevestWhite = '/logos/corvest-green-red-white-logo.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { 
      name: 'Services', 
      href: '#',
      dropdown: [
        { name: 'Investment Management', href: '/services/investment-management' },
        { name: 'Financial Planning', href: '/services/financial-planning' },
        { name: 'Retirement Planning', href: '/services/retirement-planning' },
      ]
    },
    { 
      name: 'Sectors', 
      href: '#',
      dropdown: [
        { name: 'Cryptocurrency Investment', href: '/sectors/cryptocurrency' },
        { name: 'Loans and Grants', href: '/sectors/loans-and-grants' },
        { name: 'Stock or Share', href: '/sectors/stocks' },
        { name: 'Oil and Gas', href: '/sectors/oil-and-gas' },
        { name: 'Real Estate', href: '/sectors/real-estate' },
      ]
    },
    { name: 'Investment Packages', href: '/investment-packages' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleDropdownToggle = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-navy-900/20 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group cursor-pointer">
            <img
              src={isScrolled ? corevestDark : corevestWhite}
              alt="COREVEST"
              className="h-8 sm:h-9 md:h-10 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navItems.map((item, index) => (
              <div key={item.name} className="relative group">
                {item.dropdown ? (
                  <div className="relative">
                    <button
                      className={`nav-link text-sm lg:text-base font-medium transition-colors duration-300 flex items-center space-x-1 ${
                        isScrolled 
                          ? 'text-navy-700 hover:text-orange-500' 
                          : 'text-white hover:text-orange-500'
                      }`}
                      onClick={() => handleDropdownToggle(index)}
                    >
                      <span>{item.name}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
                        activeDropdown === index ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {activeDropdown === index && (
                      <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                        {item.dropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className="block px-4 py-2 text-sm text-navy-700 hover:bg-gray-50 hover:text-orange-500 transition-colors"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`nav-link text-sm lg:text-base font-medium transition-colors duration-300 ${
                      isScrolled 
                        ? 'text-navy-700 hover:text-orange-500' 
                        : 'text-white hover:text-orange-500'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            <a 
              href="https://dashboard.corevests.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary text-sm lg:text-base py-2 px-4 lg:py-3 lg:px-6 inline-block"
            >
              Client Login
            </a>
          </nav>

          {/* Mobile Header Actions */}
          <div className="md:hidden flex items-center">
            <button
              className="p-2 rounded-lg hover:bg-black/10 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className={`h-5 w-5 sm:h-6 sm:w-6 ${isScrolled ? 'text-navy-900' : 'text-white'}`} />
              ) : (
                <Menu className={`h-5 w-5 sm:h-6 sm:w-6 ${isScrolled ? 'text-navy-900' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-200 animate-slide-up">
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item, index) => (
                <div key={item.name}>
                  {item.dropdown ? (
                    <div>
                      <button 
                        className="flex items-center justify-between w-full text-navy-700 hover:text-orange-500 font-medium py-2 text-base text-left"
                        onClick={() => setMobileDropdown(mobileDropdown === index ? null : index)}
                      >
                        {item.name}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${
                          mobileDropdown === index ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {mobileDropdown === index && (
                        <div className="ml-4 space-y-2">
                          {item.dropdown.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.name}
                              to={dropdownItem.href}
                              className="block text-navy-600 hover:text-orange-500 py-1 text-sm"
                              onClick={() => {
                                setIsMobileMenuOpen(false);
                                setMobileDropdown(null);
                              }}
                            >
                              {dropdownItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className="block text-navy-700 hover:text-orange-500 font-medium py-2 text-base"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <a 
                href="https://dashboard.corevests.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full btn-primary mt-4 py-3 text-base inline-block text-center"
              >
                Client Login
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 